import React, { useEffect, useCallback } from 'react';
import { addEventEntry } from '@printdeal/data-layer-manager';

import {
  Empty,
  Grid,
  TextInput,
  SelectField,
  Alert,
  Spinner,
  Label,
} from '@printdeal/ui-library';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormik } from 'formik';
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import OrderInList from './OrderInList';
import OrderListPagination from './OrderListPagination';
// @ts-expect-error
import { messages } from '../../translations/orders';

// Hooks
import { useOrders } from '../hooks/useOrders';
import {
  dispatchDataLayerOrderSearch,
} from '../../../../helpers/dataLayerHelpers/OrderOverviewDataLayerHelper';

const ORDERS_PER_PAGE = 5;

const OrderSearchAndResult = () => {
  const [filter, setFilter] = useQueryParam('filter', StringParam);
  const [search, setSearch] = useQueryParam('search', StringParam);
  const [pageIndex = 1, setPageIndex] = useQueryParam('page', NumberParam);
  const intl = useIntl();

  const {
    status,
    orders,
    total,
  } = useOrders({
    ordersPerPage: ORDERS_PER_PAGE,
    pageIndex,
    search,
    filter,
  });

  const handleFilter = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setFilter(event.target.value);
    setPageIndex(1);
  };

  const searchFormik = useFormik({
    initialValues: { orderSearch: search },
    onSubmit: async ({ orderSearch }) => {
      setSearch(orderSearch);
      setPageIndex(1);
    },
  });

  const registerEvent = useCallback((eventAction: string, extraData: any) => {
    addEventEntry({
      eventCategory: 'order_overview',
      eventAction,
      eventLabel: extraData && JSON.stringify(extraData),
    });
  }, []);

  const flipPage = useCallback((pageNumber: number) => {
    setPageIndex(pageNumber);
    registerEvent('use_pagination', { pageNumber });
  }, [registerEvent, setPageIndex]);

  useEffect(() => {
    if (status !== 'pending') {
      // We only want to send the event if the user actually types searchQuery
      dispatchDataLayerOrderSearch(search || '', total || 0, pageIndex, filter || 'all');
    }
  }, [search, filter, status, pageIndex, total]);

  const orderSearchMessage = intl.formatMessage(messages.orderSearchPlaceholder);

  return (
    <>
      <Grid className="pd-mb-5 pd-grid-cols-1 pd-gap-4 lg:pd-grid-cols-12">
        <Grid.Item className="lg:pd-col-span-7">
          <form onSubmit={searchFormik.handleSubmit}>
            <Label htmlFor="orderSearch" className="pd-sr-only">
              {orderSearchMessage}
            </Label>
            <TextInput
              id="orderSearch"
              type="text"
              placeholder={orderSearchMessage}
              data-cy="orderSearchInput"
              className="pd-w-full"
                // eslint-disable-next-line react/jsx-props-no-spreading
              {...searchFormik.getFieldProps('orderSearch')}
            />
          </form>
        </Grid.Item>
        <Grid.Item className="lg:pd-col-span-5">
          <Label htmlFor="filter" className="pd-sr-only">
            <FormattedMessage
              id={messages.statusFilter.id}
              defaultMessage={messages.statusFilter.defaultMessage}
            />
          </Label>
          <SelectField
            id="filter"
            name="filter"
            className="pd-w-full"
            onChange={handleFilter}
          >
            <SelectField.Option selected={!filter} value="">
              {intl.formatMessage(messages.filterAll)}
            </SelectField.Option>
            <SelectField.Option selected={filter === 'paymentNeeded'} value="paymentNeeded">
              {intl.formatMessage(messages.filterPaymentNeeded)}
            </SelectField.Option>
            <SelectField.Option selected={filter === 'needsFileDelivery'} value="needsFileDelivery">
              {intl.formatMessage(messages.filterNeedsFileDelivery)}
            </SelectField.Option>
            <SelectField.Option selected={filter === 'inProduction'} value="inProduction">
              {intl.formatMessage(messages.filterInProduction)}
            </SelectField.Option>
            <SelectField.Option selected={filter === 'shipped'} value="shipped">
              {intl.formatMessage(messages.filterShipped)}
            </SelectField.Option>
            <SelectField.Option selected={filter === 'cancelled'} value="cancelled">
              {intl.formatMessage(messages.filterCancelled)}
            </SelectField.Option>
            <SelectField.Option selected={filter === 'docReviewInProofing'} value="docReviewInProofing">
              {intl.formatMessage(messages.filterDocReviewInProofing)}
            </SelectField.Option>
          </SelectField>
        </Grid.Item>
      </Grid>

      {status === 'pending' && (
      <div className="pd-my-4">
        <Spinner className="pd-mx-auto" size="lg" />
      </div>
      )}

      {status === 'error' && (
      <Alert className="pd-mb-3" variant="error" isDismissible>
        <FormattedMessage id={messages.ordersError.id} />
      </Alert>
      )}

      {(status === 'success' && total !== undefined && total > 0) && (
      <>
        <ul className="pd-mb-6 pd-flex pd-flex-col pd-gap-6" data-cy="orderList">
          {orders?.map((order) => (
            <li className="pd-flex pd-flex-col pd-gap-1" key={order.id}>
              <OrderInList
                order={order}
              />
            </li>
          ))}
        </ul>
        <OrderListPagination
          total={total}
          pageIndex={pageIndex}
          flipPage={flipPage}
          ordersPerPage={ORDERS_PER_PAGE}
        />
      </>
      )}

      {(status === 'success' && total === 0) && (
      <Empty
        icon="search"
        title={intl.formatMessage({
          id: 'account.orders.noOrders.title',
          defaultMessage: 'Sorry, no results found.',
        })}
        description={intl.formatMessage({
          id: 'account.orders.noOrders.description',
          defaultMessage: 'we cannot find an order! :(',
        })}
      />
      )}
    </>
  );
};

export default OrderSearchAndResult;
